'use client';

import React, { useState } from 'react';

const RundownInterstitial: React.FC = () => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'listener' | 'podcaster' | 'both' | ''>('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !role) return;
    setLoading(true);
    /* Backend integration point: POST { email, role } to newsletter API */
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 900);
  };

  return (
    <section id="rundown" className="interstitial py-16 md:py-24 px-6 reveal">
      <div className="max-w-3xl mx-auto text-center relative z-10">
        {/* Label */}
        <p
          className="section-label mb-4"
          style={{ color: 'var(--color-brass)' }}
        >
          ✦ &nbsp;Free Weekly Digest
        </p>

        {/* Headline */}
        <h2
          className="font-display font-bold leading-tight mb-4"
          style={{
            fontSize: 'clamp(1.8rem, 4vw, 3rem)',
            color: 'var(--color-parchment)',
          }}
        >
          Get the Rundown.
        </h2>

        <p
          className="font-body text-base leading-relaxed mb-10 max-w-xl mx-auto"
          style={{ color: 'rgba(245,240,232,0.65)' }}
        >
          Every week: timestamped highlights, guest links, and one
          behind-the-scenes production note you won't find in the episode.
          No noise. Just craft.
        </p>

        {!submitted ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Radio segmentation */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {(
                [
                  { value: 'listener',  label: "I'm a listener" },
                  { value: 'podcaster', label: "I'm a podcaster" },
                  { value: 'both',      label: "I'm both" },
                ] as const
              ).map((opt) => (
                <label key={opt.value} className="radio-option">
                  <input
                    type="radio"
                    name="role"
                    value={opt.value}
                    checked={role === opt.value}
                    onChange={() => setRole(opt.value)}
                    className="w-4 h-4 accent-brass"
                    style={{ accentColor: 'var(--color-brass)' }}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>

            {/* Email row */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="email-input flex-1 px-4 py-3 rounded-sm text-sm"
              />
              <button
                type="submit"
                disabled={loading || !role}
                className="btn-brass px-6 py-3 rounded-sm text-sm flex items-center gap-2 justify-center disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Sending…
                  </>
                ) : (
                  'Get the Rundown →'
                )}
              </button>
            </div>

            <p
              className="font-body text-xs"
              style={{ color: 'rgba(245,240,232,0.35)' }}
            >
              No spam. Unsubscribe anytime. 4,200+ creators already subscribed.
            </p>
          </form>
        ) : (
          <div
            className="py-8 px-6 rounded-sm"
            style={{
              background: 'rgba(184,160,74,0.12)',
              border: '1px solid rgba(184,160,74,0.3)',
            }}
          >
            <p className="text-3xl mb-3">📬</p>
            <h3
              className="font-display font-bold text-xl mb-2"
              style={{ color: 'var(--color-parchment)' }}
            >
              You're in.
            </h3>
            <p
              className="font-body text-sm"
              style={{ color: 'rgba(245,240,232,0.65)' }}
            >
              First Rundown arrives next Monday. Check your inbox for a
              quick confirmation.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default RundownInterstitial;